import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import DatePickerField from "@/components/DatePickerField";
import { toast } from "sonner";
import {
  Loader2,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  Theater,
  Rocket,
  BookOpenCheck,
  X,
} from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

/**
 * First-login setup wizard shown to a freshly onboarded tenant admin.
 * Steps:
 *   0. Welcome
 *   1. Create first production (title + light details + optional show template)
 *   2. Success + enter the production
 */
export default function TenantAdminWelcome({ open, onClose, onCreated }) {
  const { user, selectProduction, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "",
    tagline: "",
    venue: "",
    director: user?.name || "",
    opening_night: "",
  });
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [created, setCreated] = useState(null);
  const [importSummary, setImportSummary] = useState(null);

  // Fetch show library once the wizard opens so the picker is instant.
  useEffect(() => {
    if (!open || templates.length) return;
    api.get("/show-library").then((r) => setTemplates(r.data)).catch(() => {});
  }, [open, templates.length]);

  if (!open) return null;

  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const canCreate = !!form.title.trim();
  const selectedTemplate = templates.find((t) => t.id === templateId);

  const create = async () => {
    if (!canCreate) return;
    setBusy(true);
    try {
      const r = await api.post("/productions", form);
      setCreated(r.data);
      // Optional: import the chosen show template's characters/scenes/songs.
      if (templateId) {
        try {
          const imp = await api.post(`/productions/${r.data.id}/import-template`, {
            template_id: templateId,
            strategy: "append",
          });
          setImportSummary(imp.data);
        } catch (impErr) {
          toast.error("Production created, but the show template import failed.");
        }
      }
      // Mark the admin's needs_setup flag as false server-side
      try { await api.post("/auth/complete-setup"); } catch (_e) { /* non-blocking */ }
      await refresh();
      onCreated && onCreated(r.data);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create production");
    } finally {
      setBusy(false);
    }
  };

  const enterProduction = () => {
    if (created?.id) {
      selectProduction(created.id);
      nav("/dashboard");
    }
    onClose && onClose();
  };

  const skip = async () => {
    try { await api.post("/auth/complete-setup"); } catch (_e) { /* non-blocking */ }
    await refresh();
    onClose && onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm overflow-y-auto"
      data-testid="tenant-welcome-overlay"
    >
      <div className="min-h-full flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0F0F0F] shadow-[0_30px_80px_-20px_rgba(245,158,11,0.35)] overflow-hidden">
          {/* Progress rail */}
          <div className="px-6 sm:px-8 pt-6 flex items-center gap-1.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? "bg-gold" : "bg-white/10"
                }`}
              />
            ))}
          </div>

          <div className="px-6 sm:px-10 py-8">
            {step === 0 && (
              <div data-testid="welcome-step-0" className="text-center">
                <div className="mx-auto w-14 h-14 rounded-full border border-gold/40 bg-gradient-to-br from-gold/20 to-transparent grid place-items-center">
                  <Sparkles className="w-6 h-6 text-gold" />
                </div>
                <h2 className="mt-5 font-display text-3xl sm:text-4xl tracking-tight">
                  Welcome to StageCall
                  {user?.name ? `, ${user.name.split(" ")[0]}` : ""}
                </h2>
                <p className="mt-3 text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
                  Your admin account for
                  {user?.theater_name ? (
                    <>
                      {" "}
                      <span className="text-zinc-200">{user.theater_name}</span>{" "}
                    </>
                  ) : (
                    " your theatre "
                  )}
                  is ready. Let&apos;s set up your first production so you can invite your company and
                  start scheduling rehearsals.
                </p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                  <StepChip n="1" title="Create your production" done />
                  <StepChip n="2" title="Invite cast & staff" />
                  <StepChip n="3" title="Schedule rehearsals" />
                </div>

                <div className="mt-8 flex items-center justify-center gap-3">
                  <button
                    onClick={skip}
                    data-testid="welcome-skip-btn"
                    className="h-10 px-4 rounded border border-white/10 text-sm text-zinc-400 hover:bg-white/5 hover:text-zinc-200"
                  >
                    Skip for now
                  </button>
                  <button
                    onClick={() => setStep(1)}
                    data-testid="welcome-start-btn"
                    style={goldBtn}
                    className="h-10 px-5 rounded font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110"
                  >
                    Let&apos;s go <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="welcome-step-1">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold inline-flex items-center gap-2">
                  <Theater className="w-3.5 h-3.5" /> Step 1 · Your first production
                </div>
                <h2 className="mt-3 font-display text-3xl tracking-tight">
                  Tell us about the show
                </h2>
                <p className="mt-2 text-sm text-zinc-400 max-w-lg">
                  Just the essentials — you can add scenes, songs, characters and posters from
                  the dashboard once you&apos;re inside.
                </p>

                <div className="mt-6 grid grid-cols-1 gap-4">
                  <Row
                    label="Production title"
                    testid="welcome-title"
                    value={form.title}
                    onChange={(v) => patch({ title: v })}
                    placeholder="Into the Woods"
                  />
                  <div>
                    <Label className="text-zinc-300 text-xs uppercase tracking-widest">
                      Short tagline (optional)
                    </Label>
                    <Textarea
                      data-testid="welcome-tagline"
                      rows={2}
                      value={form.tagline}
                      onChange={(e) => patch({ tagline: e.target.value })}
                      className="mt-2 bg-[#121212] border-white/10"
                      placeholder="A tangled tale of wishes and warnings."
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Row
                      label="Director"
                      testid="welcome-director"
                      value={form.director}
                      onChange={(v) => patch({ director: v })}
                      placeholder="Defaults to your name"
                    />
                    <div>
                      <Label className="text-zinc-300 text-xs uppercase tracking-widest">
                        Opening night
                      </Label>
                      <div className="mt-2">
                        <DatePickerField
                          testid="welcome-opening"
                          value={form.opening_night}
                          onChange={(v) => patch({ opening_night: v })}
                        />
                      </div>
                    </div>
                  </div>
                  <Row
                    label="Venue"
                    testid="welcome-venue"
                    value={form.venue}
                    onChange={(v) => patch({ venue: v })}
                    placeholder="Main stage / off-site / etc."
                  />

                  {templates.length > 0 && (
                    <div
                      data-testid="welcome-template-block"
                      className="rounded-lg border border-gold/20 bg-gradient-to-br from-gold/5 to-transparent p-4"
                    >
                      <div className="flex items-center gap-2">
                        <BookOpenCheck className="w-4 h-4 text-gold" />
                        <div className="text-[10px] uppercase tracking-[0.24em] text-gold">
                          Optional · Pre-load your show
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
                        Pick a show from the library and we&apos;ll seed your production with its
                        characters, scenes and songs. You can edit or add more from the dashboard.
                      </p>
                      <div className="mt-3">
                        <Select value={templateId} onValueChange={setTemplateId}>
                          <SelectTrigger
                            data-testid="welcome-template-select"
                            className="bg-[#121212] border-white/10 h-11"
                          >
                            <SelectValue placeholder="Choose a show (or leave blank to skip)" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-72">
                            {templates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                <div className="flex flex-col text-left">
                                  <span>{t.title}</span>
                                  <span className="text-[10px] text-zinc-500">
                                    {t.character_count} chars · {t.scene_count} scenes ·{" "}
                                    {t.song_count} songs
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedTemplate && (
                          <button
                            type="button"
                            data-testid="welcome-template-clear"
                            onClick={() => setTemplateId("")}
                            className="mt-2 text-[11px] text-zinc-500 hover:text-zinc-200"
                          >
                            Clear selection · skip preload
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-8 flex items-center justify-between">
                  <button
                    onClick={() => setStep(0)}
                    className="h-10 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    onClick={create}
                    data-testid="welcome-create-btn"
                    disabled={!canCreate || busy}
                    style={goldBtn}
                    className="h-10 px-5 rounded font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-50 hover:brightness-110"
                  >
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create production
                  </button>
                </div>
              </div>
            )}

            {step === 2 && created && (
              <div data-testid="welcome-step-2" className="text-center">
                <div className="mx-auto w-14 h-14 rounded-full border border-gold/40 bg-gradient-to-br from-gold/20 to-transparent grid place-items-center">
                  <Check className="w-6 h-6 text-gold" />
                </div>
                <h2 className="mt-5 font-display text-3xl sm:text-4xl tracking-tight">
                  You&apos;re all set
                </h2>
                <p className="mt-3 text-sm text-zinc-400 max-w-md mx-auto">
                  <span className="text-zinc-200">{created.title}</span> is live. Share your
                  enrollment code so cast and staff can join.
                </p>

                <div className="mt-6 rounded-lg border border-white/10 bg-[#0A0A0A] p-4 inline-flex items-center gap-3">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                    Enroll code
                  </div>
                  <code
                    data-testid="welcome-enroll-code"
                    className="font-mono text-gold text-lg tracking-[0.3em]"
                  >
                    {created.enroll_code}
                  </code>
                </div>

                {importSummary && (
                  <div
                    data-testid="welcome-import-summary"
                    className="mt-4 mx-auto max-w-md rounded-lg border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent p-4 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <BookOpenCheck className="w-4 h-4 text-gold" />
                      <div className="text-xs uppercase tracking-widest text-gold">
                        Pre-loaded from {importSummary.template.title}
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-zinc-300">
                      {importSummary.imported.characters} characters ·{" "}
                      {importSummary.imported.scenes} scenes ·{" "}
                      {importSummary.imported.songs} songs
                    </div>
                  </div>
                )}

                <div className="mt-8 flex items-center justify-center gap-3">
                  <button
                    onClick={enterProduction}
                    data-testid="welcome-enter-btn"
                    style={goldBtn}
                    className="h-11 px-6 rounded font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110"
                  >
                    <Rocket className="w-4 h-4" /> Enter production
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Close (X) — only from welcome/create steps */}
          {step !== 2 && (
            <button
              onClick={skip}
              data-testid="welcome-close-btn"
              className="absolute top-6 right-6 p-1.5 rounded border border-white/10 text-zinc-500 hover:text-zinc-200 hover:bg-white/5"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, testid, value, onChange, placeholder, type }) {
  return (
    <div>
      <Label className="text-zinc-300 text-xs uppercase tracking-widest">{label}</Label>
      <Input
        data-testid={testid}
        type={type || "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 bg-[#121212] border-white/10 h-11"
      />
    </div>
  );
}

function StepChip({ n, title, done }) {
  return (
    <div
      className={`rounded-lg border p-3 flex items-center gap-2.5 ${
        done ? "border-gold/40 bg-gold/5" : "border-white/10 bg-[#121212]"
      }`}
    >
      <div
        className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-semibold ${
          done ? "bg-gold text-[#0A0A0A]" : "border border-white/10 text-zinc-400"
        }`}
      >
        {n}
      </div>
      <div className="text-xs text-zinc-300">{title}</div>
    </div>
  );
}
